import type { ReactNode } from 'react';
import { Button, Card, CardBody, Chip } from '@nextui-org/react';
import { Icon } from '@iconify/react/offline';
import ArrowIcon from '@iconify-icons/tabler/arrow-right';
import type {
  CaseInput,
  CaseKind,
  CaseStatus,
  ShipmentCase,
} from 'common/src/api/shipment-cases';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert, EmptyState, KpiCard } from '../../components/ui';
import {
  caseKinds,
  caseStatuses,
  caseTotals,
  confidenceLabels,
  methods,
  outcomes,
} from './model';

type FilterField = 'client' | 'responsible' | 'kind' | 'status' | 'waiting';
type Segment = { label: string; value: number; className: string };
type SummaryCase = ShipmentCase & { pending_requests?: CaseInput[] };
const kinds: CaseKind[] = ['observation', 'claim', 'incident'];
const statuses: CaseStatus[] = ['internal', 'waiting', 'closed'];
const kindColors: Record<CaseKind, string> = {
  observation: 'bg-warning',
  claim: 'bg-danger',
  incident: 'bg-secondary',
};
const statusColors: Record<CaseStatus, string> = {
  internal: 'bg-default-400',
  waiting: 'bg-secondary',
  closed: 'bg-success',
};
const confidenceColors = {
  firm: 'bg-success',
  solid: 'bg-secondary',
  estimated: 'bg-warning',
  reference: 'bg-default-400',
};

// Shared cargo contributes once. Attribute kilos to the highest current type,
// and risk to the case with the greatest loss per source line, as in caseTotals.
function contributions(cases: ShipmentCase[], measure: 'kilos' | 'risk') {
  const rows = new Map<
    number,
    {
      value: number;
      kind: CaseKind;
      method: string;
      confidence: keyof typeof confidenceColors;
    }
  >();
  for (const record of cases.filter((record) => record.status !== 'closed')) {
    for (const item of record.items) {
      const value =
        measure === 'kilos'
          ? Number(item.kilos)
          : Number(item.kilos) *
            Math.max(
              0,
              Number(record.valuation?.benchmark_usd_kg || 0) -
                Number(record.valuation?.obtained_usd_kg || 0)
            );
      const previous = rows.get(item.source_box_id);
      if (
        !previous ||
        value > previous.value ||
        (value === previous.value &&
          kinds.indexOf(record.kind) > kinds.indexOf(previous.kind))
      ) {
        rows.set(item.source_box_id, {
          value,
          kind: record.kind,
          method: record.valuation?.methodology || '',
          confidence: record.valuation?.confidence || 'reference',
        });
      }
    }
  }
  return [...rows.values()];
}

function Segments({
  segments,
  label,
  total,
  height = 'h-1.5',
}: {
  segments: Segment[];
  label: string;
  total?: number;
  height?: string;
}) {
  const denominator =
    total ?? segments.reduce((sum, item) => sum + item.value, 0);
  return (
    <span
      role='img'
      aria-label={label}
      className={`flex w-full overflow-hidden rounded-full bg-default-100 ${height}`}
    >
      {segments
        .filter((item) => item.value > 0)
        .map((item) => (
          <span
            key={item.label}
            title={`${item.label}: ${item.value}`}
            className={`h-full shrink-0 ${item.className}`}
            style={{
              width: `${denominator > 0 ? (item.value / denominator) * 100 : 0}%`,
            }}
          />
        ))}
    </span>
  );
}

function Legend({
  segments,
  counts = false,
}: {
  segments: Segment[];
  counts?: boolean;
}) {
  return (
    <span className='flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-default-500'>
      {segments.map((item) => (
        <span key={item.label} className='inline-flex items-center gap-1.5'>
          <span
            className={`size-1.5 shrink-0 rounded-full ${item.className}`}
          />
          {item.label}
          {counts && (
            <span className='font-mono font-semibold text-foreground'>
              {item.value}
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

function Panel({
  title,
  subtitle,
  aside,
  children,
}: {
  title: string;
  subtitle?: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card shadow='sm' className='min-w-0'>
      <CardBody className='gap-4 p-6'>
        <div className='flex flex-wrap items-baseline justify-between gap-x-3 gap-y-2'>
          <p className='text-sm font-semibold'>
            {title}
            {subtitle && (
              <span className='ml-1 text-xs font-normal text-default-400'>
                · {subtitle}
              </span>
            )}
          </p>
          {aside}
        </div>
        {children}
      </CardBody>
    </Card>
  );
}

function isoWeek(date: Date) {
  const day = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
  day.setUTCDate(day.getUTCDate() + 4 - (day.getUTCDay() || 7));
  const year = day.getUTCFullYear();
  const week = Math.ceil(
    ((day.getTime() - Date.UTC(year, 0, 1)) / 86400000 + 1) / 7
  );
  return { year, week };
}

export default function CasesSummary({
  cases,
  onFilter,
}: {
  cases: SummaryCase[];
  onFilter: (field: FilterField, value: string) => void;
}) {
  const { t, currentLanguage } = useTranslation('shipmentCases');
  const locale = currentLanguage === 'en' ? 'en-US' : 'es-CL';
  const number = (value: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value);
  const money = (value: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  const active = cases.filter((record) => record.status !== 'closed');
  const closed = cases.filter((record) => record.status === 'closed');
  const financial = active.filter((record) => record.kind !== 'observation');
  const totals = caseTotals(active);
  const risk = caseTotals(financial).risk;
  const today = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const age = (date: string) =>
    Math.max(
      0,
      Math.floor(
        (Date.parse(`${today}T00:00:00Z`) - Date.parse(date)) / 86400000
      )
    );
  const unassigned = active.filter(
    (record) => record.kind !== 'observation' && !record.responsible
  );
  const unvalued = financial.filter((record) => !record.valuation);
  const inputs = active.flatMap((record) => record.pending_requests || []);
  const hasInputDetails = active.every(
    (record) => record.pending_requests !== undefined
  );
  const oldExternal = active.filter((record) =>
    record.pending_requests?.some(
      (input) =>
        input.recipient_kind === 'external' && age(input.requested_at) > 10
    )
  );
  const needsAttention = new Set(
    [...unassigned, ...oldExternal].map((record) => record.id)
  );
  const statusLegend = statuses.map((status) => ({
    label: t(caseStatuses[status]),
    value: active.filter((record) => record.status === status).length,
    className: statusColors[status],
  }));
  const kindLegend = kinds.map((kind) => ({
    label: t(caseKinds[kind]),
    value: active.filter((record) => record.kind === kind).length,
    className: kindColors[kind],
  }));
  const kgParts = contributions(active, 'kilos');
  const kgSegments = kinds.map((kind) => ({
    label: t(caseKinds[kind]),
    value: kgParts
      .filter((row) => row.kind === kind)
      .reduce((sum, row) => sum + row.value, 0),
    className: kindColors[kind],
  }));
  const riskParts = contributions(financial, 'risk');
  const methodRows = Object.entries(methods)
    .map(([key, label]) => {
      const records = financial.filter(
        (record) => record.valuation?.methodology === key
      );
      return {
        key,
        label: t(label),
        amount: riskParts
          .filter((row) => row.method === key)
          .reduce((sum, row) => sum + row.value, 0),
        records,
        confidences: [
          ...new Set(
            records.flatMap((record) =>
              record.valuation ? [record.valuation.confidence] : []
            )
          ),
        ],
      };
    })
    .filter((row) => row.records.length);

  const group = (
    records: ShipmentCase[],
    keyOf: (record: ShipmentCase) => string
  ) => {
    const result = new Map<string, ShipmentCase[]>();
    for (const record of records) {
      const key = keyOf(record);
      result.set(key, [...(result.get(key) || []), record]);
    }
    return [...result.entries()].map(([label, records]) => ({
      label,
      records,
    }));
  };
  const bars = (
    groups: { label: string; records: ShipmentCase[] }[],
    measure: 'kilos' | 'risk',
    field?: FilterField
  ) => {
    const rows = groups
      .map((row) => ({
        ...row,
        value: caseTotals(
          measure === 'risk'
            ? row.records.filter((record) => record.kind !== 'observation')
            : row.records
        )[measure],
        parts: contributions(
          measure === 'risk'
            ? row.records.filter((record) => record.kind !== 'observation')
            : row.records,
          measure
        ),
      }))
      .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
    const maximum = Math.max(...rows.map((row) => row.value), 1);
    return rows.length ? (
      <div className='flex flex-col gap-1'>
        {rows.map((row) => {
          const segments = kinds.map((kind) => ({
            label: t(caseKinds[kind]),
            value: row.parts
              .filter((part) => part.kind === kind)
              .reduce((sum, part) => sum + part.value, 0),
            className: kindColors[kind],
          }));
          const content = (
            <span className='grid w-full min-w-0 grid-cols-[minmax(0,1fr)_minmax(40px,1fr)_72px_24px] items-center gap-2 text-xs sm:grid-cols-[minmax(110px,150px)_minmax(40px,1fr)_82px_28px]'>
              <span
                title={row.label}
                className='truncate text-right text-default-500'
              >
                {t(row.label)}
              </span>
              <Segments
                segments={segments}
                total={maximum}
                height='h-3.5'
                label={`${t(row.label)}: ${measure === 'risk' ? money(row.value) : `${number(row.value)} kg`}`}
              />
              <span className='whitespace-nowrap text-right font-mono font-semibold'>
                {measure === 'risk' ? money(row.value) : number(row.value)}
              </span>
              <span className='text-right text-[10px] text-default-400'>
                {row.records.length} {t('c.')}
              </span>
            </span>
          );
          return field ? (
            <Button
              key={row.label}
              size='sm'
              variant='light'
              className='h-auto min-h-8 min-w-0 justify-start px-1 py-1'
              onPress={() => onFilter(field, row.label)}
            >
              {content}
            </Button>
          ) : (
            <div key={row.label} className='px-1 py-1.5'>
              {content}
            </div>
          );
        })}
      </div>
    ) : (
      <EmptyState title={t('No hay casos con estos filtros.')} />
    );
  };

  const lastDate =
    cases
      .map((record) => Date.parse(record.created_at))
      .filter(Number.isFinite)
      .sort((a, b) => b - a)[0] || Date.now();
  const weekStart = new Date(lastDate);
  weekStart.setUTCHours(0, 0, 0, 0);
  weekStart.setUTCDate(
    weekStart.getUTCDate() - ((weekStart.getUTCDay() || 7) - 1)
  );
  const weeks = Array.from({ length: 12 }, (_, index) => {
    const start = new Date(weekStart);
    start.setUTCDate(start.getUTCDate() - (11 - index) * 7);
    const end = start.getTime() + 7 * 86400000;
    const { year, week } = isoWeek(start);
    const records = cases.filter(
      (record) =>
        Date.parse(record.created_at) >= start.getTime() &&
        Date.parse(record.created_at) < end
    );
    return {
      label: `W${week}`,
      title: `${year} · ${t('Semana')} ${week}`,
      records,
    };
  });
  const maxWeek = Math.max(...weeks.map((week) => week.records.length), 1);
  const reasons = new Map<string, ShipmentCase[]>();
  for (const record of active)
    for (const reason of record.reasons)
      reasons.set(reason, [...(reasons.get(reason) || []), record]);
  const pendingGroups = new Map<
    string,
    {
      recipient: string;
      kind: CaseInput['recipient_kind'];
      inputs: CaseInput[];
    }
  >();
  for (const input of inputs) {
    const key = `${input.recipient_kind}:${input.recipient}`;
    const existing = pendingGroups.get(key);
    pendingGroups.set(key, {
      recipient: input.recipient,
      kind: input.recipient_kind,
      inputs: [...(existing?.inputs || []), input],
    });
  }
  const pendingRows = [...pendingGroups.values()].sort((a, b) =>
    a.kind === b.kind
      ? b.inputs.length - a.inputs.length
      : a.kind === 'external'
        ? -1
        : 1
  );
  const closureRows = Object.entries(outcomes).map(([key, label]) => ({
    label: t(label),
    value: closed.filter((record) => record.outcome === key).length,
    className:
      key === 'resolved'
        ? 'bg-success'
        : key === 'rejected'
          ? 'bg-danger'
          : 'bg-default-400',
  }));

  return (
    <div className='flex min-w-0 flex-col gap-3'>
      <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
        <KpiCard
          label={t('Casos activos')}
          value={active.length}
          detail={
            <span className='flex flex-col gap-3'>
              <span>
                {t('{{count}} cerrados en el período', {
                  count: closed.length,
                })}
              </span>
              <Segments
                segments={statusLegend}
                label={t('Distribución de casos activos por estado')}
              />
              <Legend
                segments={statusLegend.filter(
                  (item) => item.label !== t(caseStatuses.closed)
                )}
                counts
              />
            </span>
          }
        />
        <KpiCard
          label={t('Monto en riesgo')}
          value={money(risk)}
          detail={
            <span className='flex flex-col gap-3'>
              <span>{t('Reclamos y siniestros no cerrados')}</span>
              <Segments
                segments={methodRows.map((row) => ({
                  label: row.label,
                  value: row.amount,
                  className:
                    confidenceColors[row.confidences[0] || 'reference'],
                }))}
                label={t('Riesgo por metodología de valorización')}
              />
              <span className='flex flex-col gap-1.5'>
                {methodRows.map((row) => (
                  <span
                    key={row.key}
                    className='flex flex-wrap justify-between gap-x-2 gap-y-0.5 text-[11px]'
                  >
                    <span className='text-default-500'>{row.label}</span>
                    <span className='font-mono text-foreground'>
                      {money(row.amount)}
                    </span>
                    <span className='w-full text-[10px] text-default-400'>
                      {row.confidences
                        .map((confidence) => t(confidenceLabels[confidence]))
                        .join(' / ')}
                    </span>
                  </span>
                ))}
                <span className='flex justify-between gap-2 text-[11px]'>
                  <span>{t('Sin valorizar')}</span>
                  <span className='font-mono text-foreground'>
                    {unvalued.length} {t('casos')}
                  </span>
                </span>
              </span>
              <span className='text-[10px] leading-relaxed text-default-400'>
                {t(
                  'Confianza según las fuentes documentadas del benchmark y del precio obtenido.'
                )}
              </span>
            </span>
          }
        />
        <KpiCard
          label={t('Kg afectados activos')}
          value={number(totals.kilos)}
          detail={
            <span className='flex flex-col gap-3'>
              <span>{t('Carga única, sin duplicar')}</span>
              <Segments
                segments={kgSegments}
                label={t('Kilos afectados por tipo actual')}
              />
              <Legend segments={kindLegend} counts />
            </span>
          }
        />
        <KpiCard
          label={t('Requieren atención')}
          value={needsAttention.size}
          detail={
            <span className='flex flex-col gap-3'>
              <span>{t('Pendientes del equipo')}</span>
              <span className='flex flex-col gap-2'>
                <span className='flex justify-between gap-2 text-[11px]'>
                  <span>{t('Sin responsable de la pérdida')}</span>
                  <span
                    className={`font-mono font-semibold ${unassigned.length ? 'text-warning' : 'text-default-400'}`}
                  >
                    {unassigned.length}
                  </span>
                </span>
                <span className='flex justify-between gap-2 text-[11px]'>
                  <span>{t('Solicitudes externas con más de 10 días')}</span>
                  <span
                    className={`font-mono font-semibold ${oldExternal.length ? 'text-secondary' : 'text-default-400'}`}
                  >
                    {hasInputDetails ? oldExternal.length : '—'}
                  </span>
                </span>
                <span className='flex justify-between gap-2 text-[11px] text-default-400'>
                  <span>{t('Revalorización')}</span>
                  <span>{t('Pronto')}</span>
                </span>
              </span>
            </span>
          }
        />
      </div>
      {totals.overlapping.size > 0 && (
        <Alert variant='warning'>
          {t(
            'Hay carga compartida entre casos. Los kilos se cuentan una vez; el riesgo usa la mayor pérdida estimada por línea, no la suma de casos.'
          )}
        </Alert>
      )}
      <Panel
        title={t('Flujo por tipo')}
        subtitle={t('El caso conserva su historial al cambiar de tipo')}
        aside={
          <Legend
            segments={statuses.map((status) => ({
              label: t(caseStatuses[status]),
              value: 0,
              className: statusColors[status],
            }))}
          />
        }
      >
        <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
          {kinds.map((kind, index) => {
            const records = cases.filter((record) => record.kind === kind);
            const open = records.filter((record) => record.status !== 'closed');
            const value = caseTotals(open);
            return (
              <div key={kind} className='flex min-w-0 items-center gap-3'>
                <button
                  type='button'
                  className='flex min-w-0 flex-1 flex-col gap-3 rounded-medium border border-default-200 p-4 text-left transition-colors hover:bg-default-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus'
                  onClick={() => onFilter('kind', kind)}
                  aria-label={`${t('Ver casos')}: ${t(caseKinds[kind])}`}
                >
                  <span className='flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-default-500'>
                    <span
                      className={`size-2 rounded-full ${kindColors[kind]}`}
                    />
                    {t(caseKinds[kind])}
                  </span>
                  <span className='flex flex-wrap items-baseline gap-2'>
                    <span className='text-2xl font-semibold'>
                      {records.length}
                    </span>
                    <span className='text-xs text-default-400'>
                      {t('casos')} · {open.length} {t('activos')}
                    </span>
                  </span>
                  <Segments
                    segments={statuses.map((status) => ({
                      label: t(caseStatuses[status]),
                      value: records.filter(
                        (record) => record.status === status
                      ).length,
                      className: statusColors[status],
                    }))}
                    label={`${t(caseKinds[kind])}: ${t('Distribución por estado')}`}
                  />
                  <span className='flex justify-between gap-2 text-xs'>
                    <span className='text-default-500'>
                      {t('Kg afectados')}
                    </span>
                    <span className='font-mono font-semibold'>
                      {number(value.kilos)}
                    </span>
                  </span>
                  <span className='flex justify-between gap-2 text-xs'>
                    <span className='text-default-500'>
                      {t(
                        kind === 'observation'
                          ? 'Valorización estimada'
                          : 'Monto reclamado activo'
                      )}
                    </span>
                    <span className='font-mono font-semibold'>
                      {open.some((record) => record.valuation)
                        ? money(value.risk)
                        : '—'}
                    </span>
                  </span>
                </button>
                {index < kinds.length - 1 && (
                  <Icon
                    icon={ArrowIcon}
                    width={16}
                    className='hidden shrink-0 text-default-300 md:block'
                    aria-hidden='true'
                  />
                )}
              </div>
            );
          })}
        </div>
      </Panel>
      <div className='grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-2'>
        <Panel
          title={t('Por cliente')}
          subtitle={t('kg afectados por tipo')}
          aside={<Legend segments={kindLegend} />}
        >
          {bars(
            group(active, (record) => record.shipment_snapshot.receiver || '—'),
            'kilos',
            'client'
          )}
        </Panel>
        <Panel
          title={t('Por responsable de la pérdida')}
          subtitle={t('monto por tipo')}
          aside={
            <span className='text-[11px] text-default-400'>
              {t('Se atribuye tras la evaluación técnica')}
            </span>
          }
        >
          {bars(
            group(active, (record) => record.responsible || 'Sin asignar'),
            'risk',
            'responsible'
          )}
        </Panel>
        <Panel
          title={t('Por semana de ingreso')}
          subtitle={t('casos según tipo actual')}
        >
          {cases.length ? (
            <div className='flex h-40 items-end gap-1.5 pt-2 sm:gap-2'>
              {weeks.map((week) => (
                <div
                  key={week.title}
                  className='flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-2'
                  title={week.title}
                >
                  <div
                    role='img'
                    aria-label={`${week.title}: ${week.records.length} ${t('casos')}`}
                    className='flex w-full flex-col-reverse gap-0.5'
                    style={{
                      height: `${Math.max(2, (week.records.length / maxWeek) * 100)}%`,
                    }}
                  >
                    {week.records.length ? (
                      kinds.map((kind) => {
                        const count = week.records.filter(
                          (record) => record.kind === kind
                        ).length;
                        return count > 0 ? (
                          <span
                            key={kind}
                            className={`min-h-0 rounded-sm ${kindColors[kind]}`}
                            style={{ flex: count }}
                            title={`${t(caseKinds[kind])}: ${count}`}
                          />
                        ) : null;
                      })
                    ) : (
                      <span className='h-full rounded-sm bg-default-100' />
                    )}
                  </div>
                  <span className='text-[9px] text-default-400 sm:text-[10px]'>
                    {week.label}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title={t('No hay casos con estos filtros.')} />
          )}
          <Legend segments={kindLegend} />
          <p className='text-[11px] text-default-400'>
            {t('Últimas 12 semanas con respecto al último ingreso del filtro.')}
          </p>
        </Panel>
        <Panel title={t('Por motivo')} subtitle={t('kg afectados por tipo')}>
          {bars(
            [...reasons].map(([label, records]) => ({ label, records })),
            'kilos'
          )}
          <p className='text-[11px] text-default-400'>
            {t(
              'Un caso puede tener varios motivos; los grupos no se suman entre sí.'
            )}
          </p>
        </Panel>
        <Panel
          title={t('Solicitudes pendientes')}
          subtitle={t('por quién debe responder')}
          aside={
            <Chip size='sm'>
              {
                inputs.filter((input) => input.recipient_kind === 'external')
                  .length
              }{' '}
              {t('externas')}
            </Chip>
          }
        >
          {pendingRows.length ? (
            <div className='flex flex-col gap-1'>
              {pendingRows.map((row) => (
                <Button
                  key={`${row.kind}:${row.recipient}`}
                  variant='light'
                  size='sm'
                  className='h-auto min-h-9 min-w-0 justify-start px-1 py-1.5'
                  onPress={() => onFilter('waiting', row.recipient)}
                >
                  <span className='grid w-full min-w-0 grid-cols-[8px_minmax(0,1fr)_auto_24px] items-center gap-2 text-xs'>
                    <span
                      className={`size-2 rounded-full ${row.kind === 'external' ? 'bg-secondary' : 'bg-default-400'}`}
                    />
                    <span className='truncate text-left'>
                      {row.recipient}{' '}
                      <span className='text-[11px] text-default-400'>
                        · {t(row.kind === 'external' ? 'Externo' : 'Interno')}
                      </span>
                    </span>
                    <span className='text-[11px] text-default-400'>
                      {t('máx.')}{' '}
                      {Math.max(
                        ...row.inputs.map((input) => age(input.requested_at))
                      )}{' '}
                      {t('d')}
                    </span>
                    <span className='text-right font-mono font-semibold'>
                      {row.inputs.length}
                    </span>
                  </span>
                </Button>
              ))}
            </div>
          ) : (
            <EmptyState
              title={t(
                hasInputDetails
                  ? 'Sin solicitudes pendientes en el filtro actual.'
                  : 'Actualiza para ver el detalle de solicitudes pendientes.'
              )}
            />
          )}
        </Panel>
        <Panel
          title={t('Casos cerrados')}
          subtitle={t('resultado del caso')}
          aside={
            <span className='text-[11px] text-default-400'>
              {t('{{count}} en el período', { count: closed.length })}
            </span>
          }
        >
          {closed.length ? (
            <>
              <Segments
                segments={closureRows}
                height='h-2.5'
                label={t('Distribución de resultados de casos cerrados')}
              />
              <div className='flex flex-col gap-2'>
                {closureRows
                  .filter((row) => row.value > 0)
                  .map((row) => (
                    <span
                      key={row.label}
                      className='flex items-center gap-2 text-sm'
                    >
                      <span
                        className={`size-2 shrink-0 rounded-full ${row.className}`}
                      />
                      <span className='flex-1 text-default-500'>
                        {row.label}
                      </span>
                      <span className='font-mono font-semibold'>
                        {row.value}
                      </span>
                    </span>
                  ))}
              </div>
            </>
          ) : (
            <EmptyState
              title={t('No hay casos cerrados en el filtro actual.')}
            />
          )}
          <p className='text-xs leading-relaxed text-default-400'>
            {t(
              'El cierre documenta el resultado; no emite notas de crédito ni registra recuperos.'
            )}
          </p>
        </Panel>
      </div>
    </div>
  );
}
